import Alert from "controls/Alert";
import React, { createContext, ReactNode, useContext, useState } from "react";
import theme from "../assets/theme";
import { ThemeProvider } from "@mui/material/styles";

interface AlertContextType {
  setAlert: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    icon?: string
  ) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [icon, setIcon] = useState("check");
  const [type, setType] = useState<"success" | "error" | "warning">("success");
  const [title, setTitle] = useState("Alert");
  const [message, setMessage] = useState("");
  const [alertKey, setAlertKey] = useState(0); // 👈 forces re-mount

  const setAlert = (
    _type: "success" | "error" | "warning",
    _message: string,
    _title = "Alert",
    _icon = "check"
  ) => {
    setType(_type);
    setIcon(_icon);
    setTitle(_title);
    setMessage(_message);
    setAlertKey((k) => k + 1); // 🔥 force render
    setVisible(true);
  };

  const onConfirm = () => {
    setVisible(false);
  };

  return (
    <AlertContext.Provider value={{ setAlert }}>
      <ThemeProvider theme={theme}>
        <Alert
          key={alertKey} // 👈 THIS IS CRITICAL
          type={type}
          content={message}
          title={title}
          icon={icon}
          dateTime=""
          visible={visible}
          onConfirm={onConfirm}
        />
      </ThemeProvider>

      {children}
    </AlertContext.Provider>
  );
};

export const useAlertContext = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlertContext must be used within AlertProvider");
  }
  return context;
};
